import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useModal } from '@/contexts';
import whyUsLesson from '@/assets/why_us/why_us_lesson.png';
import { useI18n } from '@/i18n';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6 },
  },
};

type BrushUnderlineProps = {
  children: React.ReactNode;
  variant: 'bold' | 'thin';
  className?: string;
  markClassName?: string;
};

const brushUnderlineStyles = {
  bold: {
    svgClassName: '-bottom-[0.04em] left-[-0.12em] h-[0.33em] w-[122%]',
    viewBox: '0 0 220 34',
    mainPath:
      'M4 28 C36 14 82 11 126 10 C164 9 194 10 216 14 C219 15 219 20 215 21 C174 19 135 18 96 20 C58 22 28 26 7 33 C3 34 0 30 4 28 Z',
    texturePath:
      'M12 29 C49 21 91 18 136 17 C163 16 189 17 207 19 C174 19 134 20 93 22 C58 24 31 27 12 31 Z',
  },
  thin: {
    svgClassName: '-bottom-[0.02em] left-[-0.08em] h-[0.18em] w-[114%]',
    viewBox: '0 0 220 18',
    mainPath:
      'M3 14 C42 7 87 5 126 5 C163 5 193 7 217 11 C219 12 219 14 216 14 C175 12 137 11 95 12 C57 13 26 15 4 17 C1 17 0 15 3 14 Z',
    texturePath:
      'M9 15 C51 11 90 9 132 9 C160 9 186 10 209 12 C169 11 131 11 93 12 C57 13 30 15 9 16 Z',
  },
} as const;

const BrushUnderline: React.FC<BrushUnderlineProps> = ({
  children,
  variant,
  className = '',
  markClassName = '',
}) => {
  const style = brushUnderlineStyles[variant];

  return (
    <span className={`relative inline-block pb-[0.11em] ${className}`}>
      <span className="relative z-10">{children}</span>
      <svg
        aria-hidden="true"
        className={`absolute z-0 overflow-visible ${style.svgClassName} ${markClassName}`}
        viewBox={style.viewBox}
        preserveAspectRatio="none"
      >
        <path d={style.mainPath} fill="#F6C548" />
        <path d={style.texturePath} fill="#D9B33C" opacity="0.3" />
      </svg>
    </span>
  );
};

const TeachHeroSection: React.FC = () => {
  const { openTutorRegistration } = useModal();
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const tutorSteps = [
    {
      number: '1',
      title: isZh ? '注册' : 'Sign up',
      description: isZh
        ? '建立导师个人档案，填写你的科目、收费、地区与可授课时间。'
        : 'Create your tutor profile with your subjects, rates, location, and teaching availability.',
    },
    {
      number: '2',
      title: isZh ? '通过审核' : 'Get approved',
      description: isZh
        ? '我们的团队会在 7 个工作天内审核你的资料与证明。'
        : 'Our team reviews your profile and credentials within 7 business days.',
    },
    {
      number: '3',
      title: isZh ? '开始赚钱' : 'Start earning',
      description: isZh
        ? '在马来西亚各地开始授课，建立适合你生活节奏的补习安排。'
        : 'Teach students across Malaysia and grow a tutoring schedule that works for you.',
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white text-gray-800">
      <div className="absolute inset-x-0 top-0 h-px bg-primary-100" />

      <div className="container mx-auto max-w-6xl px-4 py-10 md:px-8 md:py-12 lg:px-12 lg:py-14 xl:px-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid items-center gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:gap-16"
        >
          <div>
            <motion.h1
              variants={itemVariants}
              className="mb-6 max-w-xl text-4xl font-bold leading-[1.08] text-black md:text-5xl"
            >
              <BrushUnderline variant="bold" markClassName="-rotate-1">{isZh ? '按你的方式' : 'Teach'}</BrushUnderline>
              {isZh ? ' 开始教学。' : ' your way.'}
              <br />
              <span>
                {isZh ? '建立你的 ' : 'Build your '}
                <BrushUnderline variant="thin" markClassName="rotate-[0.5deg]">{isZh ? '收入' : 'income'}</BrushUnderline>
                {isZh ? '。' : '.'}
              </span>
            </motion.h1>

            <motion.div variants={itemVariants} className="space-y-3 md:space-y-4">
              {tutorSteps.map((step, index) => (
                <div key={step.number} className="grid grid-cols-[4rem_1fr] items-start gap-4 sm:grid-cols-[4.5rem_1fr]">
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-2xl font-bold text-primary shadow-sm ring-1 ring-primary-100">
                    {step.number}
                  </div>
                  <div className={`min-w-0 pb-3 ${index < tutorSteps.length - 1 ? 'border-b border-primary-100' : ''}`}>
                    <h2 className="text-lg font-bold leading-snug text-gray-950 md:text-xl">
                      {step.title}
                    </h2>
                    <p className="mt-1 max-w-md text-sm leading-relaxed text-gray-600 md:text-base">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div variants={itemVariants} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                onClick={() => openTutorRegistration()}
                className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#D9B33C] bg-[#F6D15B] px-7 py-3 text-sm font-bold text-black shadow-[0_14px_32px_rgba(217,179,60,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#F2C84B] hover:shadow-[0_18px_38px_rgba(217,179,60,0.28)] focus:outline-none focus:ring-2 focus:ring-[#D9B33C] focus:ring-offset-2 sm:w-auto"
              >
                {isZh ? '免费注册导师档案' : 'Register your tutor profile for free'}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>

            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="relative">
            <div className="overflow-hidden rounded-lg shadow-xl shadow-primary/10">
              <img
                src={whyUsLesson}
                alt={isZh ? '个性化学习插图' : 'Personalized learning illustration'}
                className="h-[280px] w-full scale-[1.22] object-cover object-center lg:h-[380px]"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TeachHeroSection;
